import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, type ProfileSummary } from '../lib/api';
import {
  clearPendingAction,
  readPendingAction,
  resumePath,
  setPendingAction,
  type PendingAction,
} from '../lib/pendingActions';
import { sanitizeReturnPath } from '../lib/routes';
import { deriveSessionState, type SessionState } from '../lib/sessionState';

export type SessionContextValue = {
  loading: boolean;
  sessionState: SessionState;
  authenticated: boolean;
  keyraVerified: boolean;
  profile: ProfileSummary | null;
  refresh: () => Promise<void>;
  startKeyra: (pending?: PendingAction | string) => Promise<void>;
  resumePendingAction: () => string | null;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [keyraVerified, setKeyraVerified] = useState(false);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);

  const refresh = useCallback(async () => {
    try {
      const session = await api.getSession();
      setAuthenticated(session.authenticated);
      const p = session.profile ?? session.user ?? null;
      setProfile(p);
      setKeyraVerified(session.authenticated && Boolean(session.keyraVerified ?? p));
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 401) {
        setAuthenticated(false);
        setProfile(null);
        setKeyraVerified(false);
        return;
      }
      setAuthenticated(false);
      setProfile(null);
      setKeyraVerified(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const startKeyra = useCallback(async (pending?: PendingAction | string) => {
    if (pending && typeof pending !== 'string') {
      setPendingAction(pending);
    }
    const returnTo = sanitizeReturnPath(
      typeof pending === 'string'
        ? pending
        : pending
          ? resumePath(pending)
          : window.location.pathname + window.location.search,
    );

    const result = await api.startKeyra(returnTo);

    if (result.devMode && result.state) {
      sessionStorage.setItem('spinit_keyra_state', result.state);
      const url = new URL(result.authorizationUrl, window.location.origin);
      if (!url.searchParams.get('code')) url.searchParams.set('code', 'dev-login');
      window.location.assign(url.pathname + url.search);
      return;
    }

    window.location.assign(result.authorizationUrl);
  }, []);

  const resumePendingAction = useCallback(() => {
    const pending = readPendingAction();
    if (!pending) return null;
    clearPendingAction();
    return sanitizeReturnPath(resumePath(pending));
  }, []);

  const sessionState = deriveSessionState({
    loading,
    authenticated,
    profile,
    keyraVerified: authenticated ? keyraVerified || Boolean(profile) : false,
  });

  const value = useMemo<SessionContextValue>(
    () => ({
      loading,
      sessionState,
      authenticated,
      keyraVerified,
      profile,
      refresh,
      startKeyra,
      resumePendingAction,
    }),
    [loading, sessionState, authenticated, keyraVerified, profile, refresh, startKeyra, resumePendingAction],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession requires SessionProvider');
  return ctx;
}

export function isAccountRequired(err: unknown): boolean {
  return err instanceof ApiError && (err.code === 'ACCOUNT_REQUIRED' || err.status === 401);
}
